import { useTenant } from '@/hooks/use-tenant';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router';
import * as api from '../lib/api-client';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { FixedHeader } from '@/components/fixed-header';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import ConfettiExplosion from 'react-confetti-blast';
import { useI18nContext } from '@/i18n/i18n-react';
import { ExternalLinkButton } from '@/components/external-link-button';

export function ShopifyIntegrationSetupPage() {
  const tenant = useTenant();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const { LL, locale } = useI18nContext();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      api.createShopifyIntegration(tenant.slug, search.get('code') ?? ''),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant'] }),
  });

  return (
    <div className="container mx-auto flex items-center justify-center min-h-screen p-4 py-[150px]">
      <FixedHeader />

      {(mutation.isIdle || mutation.isPending) && (
        <Card className="max-w-lg">
          <CardHeader>
            <CardTitle>{LL.shopifyIntegration.areYouSureTitle()}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-muted-foreground text-center flex items-center justify-center gap-2">
              {
                {
                  en: (
                    <span>
                      Are you sure you want to link your Shopify account to the{' '}
                      <span className="bold">{tenant.name}</span> app?
                    </span>
                  ),
                  pt: (
                    <span>
                      Tem certeza que quer vincular sua conta Shopify ao app{' '}
                      <span className="font-bold text-foreground px-2 py-1 border border-border border-2 rounded-sm bg-muted/50 text-sm">
                        Korza
                      </span>
                      ?
                    </span>
                  ),
                }[locale]
              }
            </div>
          </CardContent>
          <CardFooter className="justify-center space-x-2">
            {mutation.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              <>
                <Link to={`/${tenant.slug}/dashboard`}>
                  <Button variant="ghost">
                    {LL.shopifyIntegration.noGoBackToDashboard()}
                  </Button>
                </Link>
                <Button onClick={() => mutation.mutate()}>
                  {LL.shopifyIntegration.yesLinkShopifyAccount()}
                </Button>
              </>
            )}
          </CardFooter>
        </Card>
      )}
      {mutation.isSuccess && (
        <Card className="max-w-md">
          <ConfettiExplosion
            {...{
              force: 0.8,
              duration: 3000,
              particleCount: 150,
              width: window.innerWidth * 2,
            }}
            style={{ marginLeft: '50%' }}
          />
          <CardHeader>
            <CardTitle>{LL.shopifyIntegration.successTitle()}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-muted-foreground text-center flex items-center justify-center gap-2">
              {LL.shopifyIntegration.successDescription({
                tenantName: tenant.name,
              })}
            </div>
          </CardContent>
          <CardFooter className="justify-center">
            <Link to={`/${tenant.slug}/dashboard`}>
              <Button>{LL.shopifyIntegration.okGoBackToDashboard()}</Button>
            </Link>
          </CardFooter>
        </Card>
      )}
      {mutation.isError && (
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>{LL.general.error()}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-muted-foreground text-center flex items-center justify-center gap-2">
              {LL.shopifyIntegration.errorDescription({
                tenantName: tenant.name,
              })}
            </div>
          </CardContent>
          <CardFooter className="justify-center gap-2 flex flex-col">
            <Link to={`/${tenant.slug}/dashboard`}>
              <Button>{LL.shopifyIntegration.okGoBackToDashboard()}</Button>
            </Link>
            <ExternalLinkButton
              variant="ghost"
              href={tenant.activateIntegrationUrl ?? ''}
            >
              {LL.shopifyIntegration.tryAgain()}
            </ExternalLinkButton>
          </CardFooter>
        </Card>
      )}
    </div>
  );
}
